import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  CheckCircle, XCircle, Plus, Edit, Eye, Search, X,
  Truck, Wallet as WalletIcon, ShoppingBag, Calendar, TrendingUp, IndianRupee,
  Phone, MessageSquare, Download, Trash2, FileText, ExternalLink,
  Archive, RotateCcw
} from 'lucide-react';
import { exportToExcel } from '@/lib/exportExcel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatINR = (n) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(n || 0));

const ORDER_STATUS_COLORS = {
  PENDING: 'bg-amber-100 text-amber-700',
  ACCEPTED: 'bg-blue-100 text-blue-700',
  PREPARING: 'bg-indigo-100 text-indigo-700',
  OUT_FOR_DELIVERY: 'bg-purple-100 text-purple-700',
  DELIVERED: 'bg-green-100 text-green-700',
  COMPLETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700'
};

const TX_TYPE_COLORS = {
  WALLET_CREDIT: 'bg-green-100 text-green-700',
  WALLET_DEBIT: 'bg-red-100 text-red-700',
  EARNING: 'bg-emerald-100 text-emerald-700',
  COLLECTION: 'bg-orange-100 text-orange-700',
  WITHDRAW: 'bg-purple-100 text-purple-700'
};

const WITHDRAW_STATUS_COLORS = {
  PENDING: 'bg-amber-100 text-amber-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  REJECTED: 'bg-red-100 text-red-700',
  PAID: 'bg-green-100 text-green-700'
};

// Date helpers
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay   = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
const todayStart = () => startOfDay(new Date());
const todayEnd   = () => endOfDay(new Date());
const yesterdayStart = () => { const x = todayStart(); x.setDate(x.getDate() - 1); return x; };
const yesterdayEnd   = () => { const x = todayEnd();   x.setDate(x.getDate() - 1); return x; };
const monthStart = () => { const x = todayStart(); x.setDate(1); return x; };
const sevenDaysStart = () => { const x = todayStart(); x.setDate(x.getDate() - 6); return x; };
const toInputDate = (d) => format(d, 'yyyy-MM-dd');

const RANGES = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'month', label: 'This Month' },
  { id: 'all', label: 'All time' },
  { id: 'custom', label: 'Custom' }
];

const PAGE_SIZE = 30;

const DeliveryAgents = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [agents, setAgents] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [verifFilter, setVerifFilter] = useState('all');
  const [workFilter, setWorkFilter] = useState('all');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [page, setPage] = useState(1);

  // Create / Edit dialog
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', address: '',
    vehicleType: '', vehicleName: '', vehicleNo: '',
    deliveryChargePerKm: '', minDeliveryCharge: '', minDeliveryChargeKM: '',
    collectionAmountMax: '',
    // Daily target bonus (rider app) — defaults: ON, 10 orders, ₹200
    dailyTargetEnabled: true, dailyTargetOrders: '10', dailyTargetBonus: '200',
    zoneId: 'none'
  });

  // Details dialog
  const [showDetails, setShowDetails] = useState(false);
  const [detailsAgent, setDetailsAgent] = useState(null);
  const [detailsTab, setDetailsTab] = useState('orders');
  const [detailsOrders, setDetailsOrders] = useState([]);
  const [detailsTxns, setDetailsTxns] = useState([]);
  const [detailsStats, setDetailsStats] = useState(null);
  const [detailsWithdrawals, setDetailsWithdrawals] = useState([]);
  const [detailsDocuments, setDetailsDocuments] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Withdrawal request review (withdrawals tab) — one row editable at a time
  const [editingWithdrawalId, setEditingWithdrawalId] = useState(null);
  const [withdrawRemarksDraft, setWithdrawRemarksDraft] = useState('');
  const [withdrawRefDraft, setWithdrawRefDraft] = useState('');
  const [withdrawSavingId, setWithdrawSavingId] = useState(null);

  // Transaction-form state (wallet tab)
  const todayInputValue = () => format(new Date(), 'yyyy-MM-dd');
  const [txnForm, setTxnForm] = useState({
    type: 'COLLECTION_SETTLEMENT',
    amount: '',
    paymentMethod: 'CASH',
    settlementDate: format(new Date(), 'yyyy-MM-dd'),
    note: ''
  });
  const [txnSaving, setTxnSaving] = useState(false);

  // Inline Max Collection editing (wallet tab)
  const [maxEditing, setMaxEditing] = useState(false);
  const [maxValue, setMaxValue] = useState('');
  const [maxSaving, setMaxSaving] = useState(false);

  // ------------------ DELETED / RESTORE PARTNERS ------------------
  const [showDeletedDialog, setShowDeletedDialog] = useState(false);
  const [deletedAgents, setDeletedAgents] = useState([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [deletedSearch, setDeletedSearch] = useState('');
  const [deletedPage, setDeletedPage] = useState(1);
  const [deletedTotal, setDeletedTotal] = useState(0);
  const [deletedTotalPages, setDeletedTotalPages] = useState(1);
  const [restoringId, setRestoringId] = useState(null);

  // Order tab filters (inside details modal)
  const [oSearch, setOSearch] = useState('');
  const [oStatus, setOStatus] = useState('all');
  const [oRest, setORest] = useState('all');
  const [oRange, setORange] = useState('all');
  const [oCustomFrom, setOCustomFrom] = useState(toInputDate(new Date()));
  const [oCustomTo, setOCustomTo] = useState(toInputDate(new Date()));
  const [oPage, setOPage] = useState(1);

  // ============ FETCHERS ============
  const fetchAgents = useCallback(async () => {
    try {
      const [a, z] = await Promise.all([
        axios.get(`${API}/delivery-agents`),
        axios.get(`${API}/zones`)
      ]);
      setAgents(a.data || []);
      setZones(z.data || []);
    } catch (err) {
      toast.error('Failed to fetch delivery agents');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  // ============ AGENT-LEVEL STATS (for top cards) ============
  const agentStats = useMemo(() => {
    let online = 0, pending = 0, earnings = 0, wallet = 0;
    for (const a of agents) {
      if (a.workStatus === 1 || a.workStatus === true) online++;
      if (a.verificationStatus === 'PENDING') pending++;
      earnings += Number(a.earningAmount || 0);
      wallet   += Number(a.wallet || 0);
    }
    return { total: agents.length, online, pending, earnings, wallet };
  }, [agents]);

  // ============ FILTERED AGENT LIST ============
  const filteredAgents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return agents.filter((a) => {
      if (verifFilter !== 'all' && a.verificationStatus !== verifFilter) return false;
      if (workFilter !== 'all') {
        const isOnline = a.workStatus === 1 || a.workStatus === true;
        if (workFilter === 'online' && !isOnline) return false;
        if (workFilter === 'offline' && isOnline) return false;
      }
      if (zoneFilter !== 'all') {
        if (zoneFilter === 'unassigned' && a.zoneId) return false;
        if (zoneFilter !== 'unassigned' && String(a.zoneId || '') !== zoneFilter) return false;
      }
      if (q) {
        const hay = [a.name, a.phone, a.email, a.vehicleType, a.vehicleNo, a.zone?.name]
          .filter(Boolean).map((v) => String(v).toLowerCase());
        if (!hay.some((v) => v.includes(q))) return false;
      }
      return true;
    });
  }, [agents, search, verifFilter, workFilter, zoneFilter]);

  useEffect(() => { setPage(1); }, [search, verifFilter, workFilter, zoneFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredAgents.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedAgents = filteredAgents.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // ============ DIALOG HANDLERS ============
  const openCreateDialog = () => {
    setEditMode(false);
    setCurrentAgent(null);
    setFormData({
      name: '', phone: '', email: '', address: '',
      vehicleType: '', vehicleName: '', vehicleNo: '',
      deliveryChargePerKm: '', minDeliveryCharge: '', minDeliveryChargeKM: '',
      collectionAmountMax: '',
      dailyTargetEnabled: true, dailyTargetOrders: '10', dailyTargetBonus: '200',
      zoneId: 'none'
    });
    setShowDialog(true);
  };

  const openEditDialog = (agent) => {
    setEditMode(true);
    setCurrentAgent(agent);
    setFormData({
      name: agent.name || '',
      phone: agent.phone || '',
      email: agent.email || '',
      address: agent.address || '',
      vehicleType: agent.vehicleType || '',
      vehicleName: agent.vehicleName || '',
      vehicleNo: agent.vehicleNo || '',
      deliveryChargePerKm: agent.deliveryChargePerKm || '',
      minDeliveryCharge: agent.minDeliveryCharge || '',
      minDeliveryChargeKM: agent.minDeliveryChargeKM || '',
      collectionAmountMax:
        agent.collectionAmountMax !== undefined && agent.collectionAmountMax !== null
          ? String(agent.collectionAmountMax)
          : '',
      dailyTargetEnabled: agent.dailyTargetEnabled !== false,
      dailyTargetOrders: String(agent.dailyTargetOrders ?? 10),
      dailyTargetBonus: String(agent.dailyTargetBonus ?? 200),
      zoneId: agent.zoneId || 'none'
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, zoneId: formData.zoneId === 'none' ? '' : formData.zoneId };
      // collectionAmountMax: send as number (or omit if blank)
      if (payload.collectionAmountMax === '' || payload.collectionAmountMax === null || payload.collectionAmountMax === undefined) {
        delete payload.collectionAmountMax;
      } else {
        payload.collectionAmountMax = Number(payload.collectionAmountMax);
      }
      // Daily target bonus
      const targetOrders = Number(payload.dailyTargetOrders);
      const targetBonus = Number(payload.dailyTargetBonus);
      if (payload.dailyTargetEnabled) {
        if (!Number.isInteger(targetOrders) || targetOrders < 1 || targetOrders > 500) {
          toast.error('Daily target orders must be a whole number from 1 to 500');
          return;
        }
        if (!Number.isFinite(targetBonus) || targetBonus < 0 || targetBonus > 100000) {
          toast.error('Daily target bonus must be between ₹0 and ₹1,00,000');
          return;
        }
      }
      payload.dailyTargetEnabled = !!payload.dailyTargetEnabled;
      payload.dailyTargetOrders = Number.isInteger(targetOrders) && targetOrders >= 1 ? targetOrders : 10;
      payload.dailyTargetBonus = Number.isFinite(targetBonus) && targetBonus >= 0 ? targetBonus : 200;
      if (editMode && currentAgent) {
        await axios.patch(`${API}/delivery-agents/${currentAgent.id}`, payload);
        toast.success('Delivery agent updated successfully');
      } else {
        await axios.post(`${API}/delivery-agents`, payload);
        toast.success('Delivery agent created successfully');
      }
      setShowDialog(false);
      fetchAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save agent');
    }
  };

  const updateAgentStatus = async (id, verificationStatus) => {
    try {
      await axios.patch(`${API}/delivery-agents/${id}`, { verificationStatus });
      toast.success('Delivery agent updated successfully');
      fetchAgents();
    } catch (err) {
      toast.error('Failed to update agent');
    }
  };

  // The download proxy route (GET /delivery-agents/:id/documents/:field/download)
  // is behind authMiddleware like every other admin API call, but a plain
  // <a href=... download> is a bare browser navigation — it doesn't carry
  // the Authorization header axios attaches (see AuthContext's
  // axios.defaults.headers.common), so it would just 401. Fetch the file
  // via axios (auth header included) as a blob instead, then hand the
  // browser a same-origin blob: URL to save — that also sidesteps the
  // cross-origin `download`-attribute restriction the direct S3 URL would
  // hit for the "View" link.
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const downloadDocument = async (doc) => {
    if (!detailsAgent) return;
    setDownloadingDoc(doc.field);
    try {
      const res = await axios.get(
        `${API}/delivery-agents/${detailsAgent.id}/documents/${doc.field}/download`,
        { responseType: 'blob' }
      );
      const blobUrl = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      const ext = (doc.url || '').split('.').pop()?.split(/[?#]/)[0].slice(0, 5) || 'jpg';
      link.download = `${(detailsAgent.name || 'rider').replace(/[^a-z0-9]+/gi, '_')}_${doc.field}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error('Failed to download document');
    } finally {
      setDownloadingDoc(null);
    }
  };

  const [deletingAgentId, setDeletingAgentId] = useState(null);
  const deleteAgent = async (agent) => {
    if (!window.confirm(`Delete rider "${agent.name || 'this rider'}"? This can't be undone from here.`)) {
      return;
    }
    setDeletingAgentId(agent.id);
    try {
      await axios.delete(`${API}/delivery-agents/${agent.id}`);
      toast.success('Rider deleted');
      setAgents((prev) => prev.filter((a) => a.id !== agent.id));
      if (detailsAgent?.id === agent.id) setShowDetails(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete rider');
    } finally {
      setDeletingAgentId(null);
    }
  };

  // ============ DELETED / RESTORE PARTNERS ============
  const fetchDeletedAgents = async (targetPage = deletedPage, query = deletedSearch) => {
    try {
      setDeletedLoading(true);
      const response = await axios.get(`${API}/delivery-agents/deleted`, {
        params: { page: targetPage, limit: PAGE_SIZE, q: query || undefined }
      });
      const { data = [], pagination = {} } = response.data || {};
      setDeletedAgents(data);
      setDeletedTotal(pagination.total || 0);
      setDeletedTotalPages(pagination.totalPages || 1);
    } catch (err) {
      toast.error('Failed to fetch deleted partners');
    } finally {
      setDeletedLoading(false);
    }
  };

  const openDeletedDialog = () => {
    setShowDeletedDialog(true);
    setDeletedPage(1);
    fetchDeletedAgents(1, deletedSearch);
  };

  // Re-fetch whenever the deleted dialog's page or (debounced) search changes.
  useEffect(() => {
    if (!showDeletedDialog) return;
    const t = setTimeout(() => {
      fetchDeletedAgents(deletedPage, deletedSearch);
    }, deletedSearch ? 400 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [showDeletedDialog, deletedPage, deletedSearch]);

  const handleRestore = async (agent) => {
    if (!window.confirm(`Restore "${agent.name || 'this partner'}"? They will reappear in the active delivery agents list.`)) {
      return;
    }
    setRestoringId(agent.id);
    try {
      await axios.post(`${API}/delivery-agents/${agent.id}/restore`);
      toast.success(`"${agent.name || 'Partner'}" restored`);
      fetchDeletedAgents();
      fetchAgents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to restore partner');
    } finally {
      setRestoringId(null);
    }
  };

  // ⭐ Manual online/offline override — an admin may need to force an agent
  // offline (e.g. reported unwell, phone lost) without waiting for them to
  // toggle it themselves in the partner app. Flips the same `workStatus`
  // field the partner app's own online/offline switch writes, via the
  // existing generic PATCH endpoint, so it's the single source of truth
  // the delivery-agent broadcast filters in foodk_api already read.
  const [workSavingId, setWorkSavingId] = useState(null);
  const toggleWorkStatus = async (agent) => {
    const isOnline = agent.workStatus === 1 || agent.workStatus === true;
    const next = !isOnline;
    setWorkSavingId(agent.id);
    try {
      await axios.patch(`${API}/delivery-agents/${agent.id}`, { workStatus: next });
      setAgents((prev) => prev.map((a) => (a.id === agent.id ? { ...a, workStatus: next } : a)));
      toast.success(`${agent.name || 'Agent'} is now ${next ? 'online' : 'offline'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update work status');
    } finally {
      setWorkSavingId(null);
    }
  };

  // ============ COLLECTION / TRANSACTIONS ============
  const refreshAgentBalances = async (agentId) => {
    try {
      const r = await axios.get(`${API}/delivery-agents/${agentId}`);
      setDetailsAgent((prev) => (prev && prev.id === agentId ? { ...prev, ...r.data } : prev));
      setAgents((prev) => prev.map((a) => (a.id === agentId ? { ...a, ...r.data } : a)));
    } catch (_) { /* ignore */ }
  };

  const submitTransaction = async (e) => {
    e?.preventDefault();
    if (!detailsAgent) return;
    const amt = Number(txnForm.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a positive amount');
      return;
    }
    setTxnSaving(true);
    try {
      const r = await axios.post(`${API}/delivery-agents/${detailsAgent.id}/transactions`, {
        type: txnForm.type,
        amount: amt,
        paymentMethod: txnForm.paymentMethod || undefined,
        settlementDate: txnForm.settlementDate || undefined,
        note: txnForm.note || undefined
      });
      toast.success('Transaction recorded');
      // refresh transactions list
      const t = await axios.get(`${API}/delivery-agents/${detailsAgent.id}/transactions`);
      setDetailsTxns(t.data || []);
      // update local agent balances
      if (r.data?.agent) {
        setDetailsAgent((prev) => prev ? { ...prev, ...r.data.agent } : prev);
        setAgents((prev) => prev.map((a) =>
          a.id === detailsAgent.id ? { ...a, ...r.data.agent } : a
        ));
      } else {
        await refreshAgentBalances(detailsAgent.id);
      }
      setTxnForm({ type: 'COLLECTION_SETTLEMENT', amount: '', paymentMethod: 'CASH', settlementDate: todayInputValue(), note: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record transaction');
    } finally {
      setTxnSaving(false);
    }
  };

  const saveMaxCollection = async () => {
    if (!detailsAgent) return;
    const v = maxValue.trim() === '' ? 0 : Number(maxValue);
    if (!Number.isFinite(v) || v < 0) {
      toast.error('Enter a non-negative number');
      return;
    }
    setMaxSaving(true);
    try {
      await axios.patch(`${API}/delivery-agents/${detailsAgent.id}`, {
        collectionAmountMax: v
      });
      toast.success('Max Collection updated');
      setDetailsAgent((prev) => prev ? { ...prev, collectionAmountMax: v } : prev);
      setAgents((prev) => prev.map((a) =>
        a.id === detailsAgent.id ? { ...a, collectionAmountMax: v } : a
      ));
      setMaxEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update max');
    } finally {
      setMaxSaving(false);
    }
  };

  // ============ DETAILS / ORDER HISTORY MODAL ============
  const openDetails = async (agent) => {
    setDetailsAgent(agent);
    setDetailsTab('orders');
    setDetailsOrders([]);
    setDetailsTxns([]);
    setDetailsStats(null);
    setDetailsWithdrawals([]);
    setDetailsDocuments([]);
    setEditingWithdrawalId(null);
    setOSearch(''); setOStatus('all'); setORest('all'); setORange('all'); setOPage(1);
    setShowDetails(true);
    setDetailsLoading(true);
    try {
      const [o, t, s, w, d] = await Promise.all([
        axios.get(`${API}/delivery-agents/${agent.id}/orders`),
        axios.get(`${API}/delivery-agents/${agent.id}/transactions`),
        axios.get(`${API}/delivery-agents/${agent.id}/stats`),
        axios.get(`${API}/delivery-agents/${agent.id}/withdrawals`),
        axios.get(`${API}/delivery-agents/${agent.id}/documents`)
      ]);
      setDetailsOrders(o.data || []);
      setDetailsTxns(t.data || []);
      setDetailsStats(s.data || null);
      setDetailsWithdrawals(w.data || []);
      setDetailsDocuments(d.data?.documents || []);
    } catch (err) {
      toast.error('Failed to load details');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Deep link support — e.g. the dashboard's "Withdraw Requests" card links
  // here as /delivery-agents?agentId=<id>&tab=withdrawals so a click lands
  // straight on that agent's details modal with the right tab open.
  useEffect(() => {
    const agentId = searchParams.get('agentId');
    if (!agentId || agents.length === 0) return;

    const agent = agents.find((a) => a.id === agentId);
    if (!agent) return;

    const tab = searchParams.get('tab');
    openDetails(agent).then(() => {
      if (tab) setDetailsTab(tab);
    });

    // Drop the params so refreshing / navigating back doesn't re-trigger it.
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents, searchParams]);

  // ============ WITHDRAWAL REQUESTS ============
  const startEditingWithdrawal = (w) => {
    setEditingWithdrawalId(w.id);
    setWithdrawRemarksDraft(w.remarks || '');
    setWithdrawRefDraft(w.transactionRef || '');
  };

  const updateWithdrawal = async (withdrawal, status) => {
    if (!detailsAgent) return;
    setWithdrawSavingId(withdrawal.id);
    try {
      const r = await axios.patch(
        `${API}/delivery-agents/${detailsAgent.id}/withdrawals/${withdrawal.id}`,
        {
          status,
          remarks: withdrawRemarksDraft,
          transactionRef: withdrawRefDraft || undefined
        }
      );
      toast.success('Withdrawal request updated');
      setDetailsWithdrawals((prev) =>
        prev.map((w) => (w.id === withdrawal.id ? { ...w, ...r.data?.data } : w))
      );
      setEditingWithdrawalId(null);
      // status changes can move money (REJECTED refunds, re-opening re-holds)
      await refreshAgentBalances(detailsAgent.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update withdrawal request');
    } finally {
      setWithdrawSavingId(null);
    }
  };

  // Order modal date window
  const oDateWindow = useMemo(() => {
    switch (oRange) {
      case 'today':     return { from: todayStart(),     to: todayEnd() };
      case 'yesterday': return { from: yesterdayStart(), to: yesterdayEnd() };
      case 'last7':     return { from: sevenDaysStart(), to: todayEnd() };
      case 'month':     return { from: monthStart(),     to: todayEnd() };
      case 'custom':    return {
        from: oCustomFrom ? startOfDay(new Date(oCustomFrom)) : null,
        to:   oCustomTo   ? endOfDay(new Date(oCustomTo))     : null
      };
      default: return { from: null, to: null };
    }
  }, [oRange, oCustomFrom, oCustomTo]);

  // Filtered orders inside modal
  const filteredAgentOrders = useMemo(() => {
    const q = oSearch.trim().toLowerCase();
    return detailsOrders.filter((o) => {
      if (oDateWindow.from && new Date(o.createdAt) < oDateWindow.from) return false;
      if (oDateWindow.to && new Date(o.createdAt) > oDateWindow.to) return false;
      if (oStatus !== 'all' && o.status !== oStatus) return false;
      if (oRest !== 'all') {
        const rid = o.resturant?._id || o.resturantId;
        if (String(rid) !== oRest) return false;
      }
      if (q) {
        const hay = [o.orderNumber, o.user?.name, o.user?.phone, o.resturant?.name]
          .filter(Boolean).map((v) => String(v).toLowerCase());
        if (!hay.some((v) => v.includes(q))) return false;
      }
      return true;
    });
  }, [detailsOrders, oSearch, oStatus, oRest, oDateWindow]);

  useEffect(() => { setOPage(1); }, [oSearch, oStatus, oRest, oRange, oCustomFrom, oCustomTo]);

  const oTotalPages = Math.max(1, Math.ceil(filteredAgentOrders.length / PAGE_SIZE));
  const oCurrentPage = Math.min(oPage, oTotalPages);
  const oPagedOrders = filteredAgentOrders.slice((oCurrentPage - 1) * PAGE_SIZE, oCurrentPage * PAGE_SIZE);

  // Restaurant options inside modal
  const oRestaurantOptions = useMemo(() => {
    const map = new Map();
    for (const o of detailsOrders) {
      const r = o.resturant;
      if (r?._id && r?.name && !map.has(String(r._id))) map.set(String(r._id), r.name);
    }
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [detailsOrders]);

  // Filtered totals (for footer)
  const filteredOrderRevenue = useMemo(() => {
    let r = 0;
    for (const o of filteredAgentOrders) if (o.status !== 'CANCELLED') r += Number(o.grandTotal || 0);
    return r;
  }, [filteredAgentOrders]);

  // ============ RENDER HELPERS ============
  const verifBadge = (s) => {
    const v = { PENDING: 'bg-yellow-100 text-yellow-800', APPROVED: 'bg-green-100 text-green-800', REJECTED: 'bg-red-100 text-red-800' };
    return <Badge className={`${v[s] || v.PENDING} border-0`}>{s || 'PENDING'}</Badge>;
  };
  const workBadge = (w) => {
    const isOnline = w === 1 || w === true;
    return isOnline
      ? <Badge className="bg-green-100 text-green-800 border-0">Online</Badge>
      : <Badge className="bg-gray-100 text-gray-700 border-0">Offline</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  const statCards = [
    { id: 'total',    label: 'Total Agents',         value: agentStats.total,    icon: Truck,        color: '#C2410C', bg: '#FFF7ED', display: agentStats.total },
    { id: 'online',   label: 'Online Now',           value: agentStats.online,   icon: CheckCircle,  color: '#059669', bg: '#ECFDF5', display: agentStats.online },
    { id: 'pending',  label: 'Pending Verification', value: agentStats.pending,  icon: XCircle,      color: '#F59E0B', bg: '#FFFBEB', display: agentStats.pending },
    { id: 'earnings', label: 'Total Earnings',       value: agentStats.earnings, icon: TrendingUp,   color: '#7C3AED', bg: '#F5F3FF', display: `₹${formatINR(agentStats.earnings)}` },
    { id: 'wallet',   label: 'Total Wallet',         value: agentStats.wallet,   icon: WalletIcon,   color: '#0EA5E9', bg: '#F0F9FF', display: `₹${formatINR(agentStats.wallet)}` }
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8" style={{ fontFamily: 'Public Sans, sans-serif' }}>
      {/* HEADER */}
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#0F172A' }}>
            Delivery Agents
          </h1>
          <p className="text-gray-600">Manage and verify delivery agent applications</p>
        </div>
        <div className="flex items-center gap-2">
        <Button
          variant="outline"
          data-testid="export-agents-button"
          onClick={() => {
            const rows = agents.map((a) => ({
              Name: a.name || '',
              Phone: a.phone || '',
              Vehicle: a.vehicleType || '',
              'Vehicle No': a.vehicleNo || '',
              Wallet: a.wallet || 0,
              Earnings: a.earningAmount || 0,
              'Work Status': a.workStatus ? 'Online' : 'Offline',
              Verification: a.verificationStatus || 'PENDING',
            }));
            if (!exportToExcel(rows, 'delivery-agents')) {
              toast.error('No delivery agents to export');
            }
          }}
        >
          <Download size={16} className="mr-2" />
          Export
        </Button>
        <Button
          variant="outline"
          data-testid="deleted-agents-button"
          onClick={openDeletedDialog}
        >
          <Archive size={16} className="mr-2" />
          Deleted Partners
        </Button>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-agent-button" onClick={openCreateDialog} style={{ backgroundColor: '#C2410C' }} className="text-white">
              <Plus size={18} className="mr-2" />Add Agent
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Delivery Agent' : 'Create New Delivery Agent'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Full Name *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
                <div><Label>Phone *</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required /></div>
              </div>
              <div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
              <div><Label>Address</Label><Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} /></div>
              <div>
                <Label>Zone</Label>
                <Select value={formData.zoneId} onValueChange={(v) => setFormData({ ...formData, zoneId: v })}>
                  <SelectTrigger data-testid="agent-zone-select"><SelectValue placeholder="Select a zone" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— No zone —</SelectItem>
                    {zones.map((z) => (
                      <SelectItem key={z._id} value={z._id}>{z.zoneName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div><Label>Vehicle Type</Label><Input placeholder="Bike, Car..." value={formData.vehicleType} onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })} /></div>
                <div><Label>Vehicle Name</Label><Input value={formData.vehicleName} onChange={(e) => setFormData({ ...formData, vehicleName: e.target.value })} /></div>
                <div><Label>Vehicle Number</Label><Input value={formData.vehicleNo} onChange={(e) => setFormData({ ...formData, vehicleNo: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div><Label>Delivery Charge / KM</Label><Input value={formData.deliveryChargePerKm} onChange={(e) => setFormData({ ...formData, deliveryChargePerKm: e.target.value })} /></div>
                <div><Label>Min Delivery Charge</Label><Input value={formData.minDeliveryCharge} onChange={(e) => setFormData({ ...formData, minDeliveryCharge: e.target.value })} /></div>
                <div><Label>Min Charge Within KM</Label><Input value={formData.minDeliveryChargeKM} onChange={(e) => setFormData({ ...formData, minDeliveryChargeKM: e.target.value })} /></div>
              </div>
              <div>
                <Label>Max Collection Amount (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g., 5000"
                  value={formData.collectionAmountMax}
                  onChange={(e) =>
                    setFormData({ ...formData, collectionAmountMax: e.target.value })
                  }
                  data-testid="agent-collection-amount-max"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum cash this agent is allowed to hold before they must settle.
                </p>
              </div>

              {/* Daily target bonus */}
              <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-4 space-y-3" data-testid="agent-daily-target">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Label htmlFor="agent-daily-target-switch" className="font-semibold">Daily Target Bonus</Label>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Extra bonus credited to the rider's wallet once they complete the target number of
                      orders in a day. Progress resets at midnight (IST).
                    </p>
                  </div>
                  <Switch
                    id="agent-daily-target-switch"
                    checked={!!formData.dailyTargetEnabled}
                    onCheckedChange={(v) => setFormData({ ...formData, dailyTargetEnabled: v })}
                    data-testid="agent-daily-target-switch"
                  />
                </div>
                {formData.dailyTargetEnabled && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="agent-daily-target-orders">Orders per day</Label>
                      <Input
                        id="agent-daily-target-orders"
                        type="number"
                        min="1"
                        max="500"
                        step="1"
                        inputMode="numeric"
                        value={formData.dailyTargetOrders}
                        onChange={(e) => setFormData({ ...formData, dailyTargetOrders: e.target.value.replace(/[^0-9]/g, '') })}
                        data-testid="agent-daily-target-orders"
                      />
                    </div>
                    <div>
                      <Label htmlFor="agent-daily-target-bonus">Bonus amount (₹)</Label>
                      <Input
                        id="agent-daily-target-bonus"
                        type="number"
                        min="0"
                        step="1"
                        inputMode="decimal"
                        value={formData.dailyTargetBonus}
                        onChange={(e) => setFormData({ ...formData, dailyTargetBonus: e.target.value })}
                        data-testid="agent-daily-target-bonus"
                      />
                    </div>
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full" style={{ backgroundColor: '#C2410C' }}>
                {editMode ? 'Update Agent' : 'Create Agent'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* STAT CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-testid="agents-stats">
        {statCards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.id} className="bg-white rounded-xl border border-gray-200 p-5" data-testid={`agent-stat-${c.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 rounded-lg" style={{ backgroundColor: c.bg }}>
                  <Icon size={20} style={{ color: c.color }} />
                </div>
              </div>
              <h3 className="text-2xl font-bold" style={{ color: '#0F172A' }}>{c.display}</h3>
              <p className="text-sm text-gray-600 mt-1">{c.label}</p>
            </div>
          );
        })}
      </div>

      {/* FILTER BAR */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="agents-filter-bar">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="agents-search-input"
            placeholder="Search name, phone, email or vehicle..."
            className="pl-9 pr-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={16} />
            </button>
          )}
        </div>

        <Select value={verifFilter} onValueChange={setVerifFilter}>
          <SelectTrigger className="w-[170px]" data-testid="agents-verif-select"><SelectValue placeholder="Verification" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All verifications</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="APPROVED">Approved</SelectItem>
            <SelectItem value="REJECTED">Rejected</SelectItem>
          </SelectContent>
        </Select>

        <Select value={workFilter} onValueChange={setWorkFilter}>
          <SelectTrigger className="w-[150px]" data-testid="agents-work-select"><SelectValue placeholder="Work status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
          </SelectContent>
        </Select>

        <Select value={zoneFilter} onValueChange={setZoneFilter}>
          <SelectTrigger className="w-[180px]" data-testid="agents-zone-select"><SelectValue placeholder="Zone" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All zones</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {zones.map((z) => (
              <SelectItem key={z._id} value={z._id}>{z.zoneName}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(search || verifFilter !== 'all' || workFilter !== 'all' || zoneFilter !== 'all') && (
          <Button variant="outline" size="sm" data-testid="agents-clear-filters" onClick={() => {
            setSearch(''); setVerifFilter('all'); setWorkFilter('all'); setZoneFilter('all');
          }}>Clear filters</Button>
        )}

        <div className="ml-auto text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{filteredAgents.length}</span> of {agents.length} agents
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="delivery-agents-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Agent</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Phone</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Vehicle</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Zone</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Wallet</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Earnings</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Today's Target</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Work</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Verification</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pagedAgents.map((agent) => (
                <tr key={agent.id} className="hover:bg-gray-50" data-testid={`agent-row-${agent.id}`}>
                  <td className="px-5 py-3">
                    <p className="font-medium text-gray-900">{agent.name || 'N/A'}</p>
                    <p className="text-xs text-gray-500">{agent.email || '-'}</p>
                  </td>
                  <td className="px-5 py-3 text-gray-900">{agent.phone || '-'}</td>
                  <td className="px-5 py-3 text-sm">
                    {agent.vehicleType || '-'}
                    {agent.vehicleNo ? <p className="text-xs text-gray-500">{agent.vehicleNo}</p> : null}
                  </td>
                  <td className="px-5 py-3 text-sm">
                    {agent.zone ? (
                      <Badge className="bg-blue-100 text-blue-800 border-0">{agent.zone.name}</Badge>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-5 py-3 text-gray-900">₹{formatINR(agent.wallet)}</td>
                  <td className="px-5 py-3 text-gray-900">₹{formatINR(agent.earningAmount)}</td>
                  <td className="px-5 py-3 text-sm" data-testid={`agent-daily-target-${agent.id}`}>
                    {agent.dailyTargetEnabled === false && !(agent.dailyTargetToday?.bonusCredited > 0) ? (
                      <span className="text-xs text-gray-400">Off</span>
                    ) : (() => {
                      const target = Number(agent.dailyTargetOrders || 10);
                      const done = Number(agent.dailyTargetToday?.completed || 0);
                      const credited = Number(agent.dailyTargetToday?.bonusCredited || 0);
                      const pct = Math.min(100, Math.round((done / target) * 100));
                      return (
                        <div className="min-w-[110px]">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-semibold text-gray-900">{done}/{target}</span>
                            <span className="text-gray-500">₹{formatINR(agent.dailyTargetBonus)}</span>
                          </div>
                          <div className="mt-1 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${credited > 0 ? 'bg-green-600' : 'bg-orange-500'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          {credited > 0 && (
                            <p className="mt-1 text-[11px] font-medium text-green-700">Bonus ₹{formatINR(credited)} paid</p>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2" data-testid={`work-toggle-cell-${agent.id}`}>
                      <Switch
                        checked={agent.workStatus === 1 || agent.workStatus === true}
                        onCheckedChange={() => toggleWorkStatus(agent)}
                        disabled={workSavingId === agent.id}
                        data-testid={`work-toggle-${agent.id}`}
                      />
                      {workBadge(agent.workStatus)}
                    </div>
                  </td>
                  <td className="px-5 py-3">{verifBadge(agent.verificationStatus)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" data-testid={`view-agent-${agent.id}`} onClick={() => openDetails(agent)}>
                        <Eye size={14} className="mr-1" />View
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`edit-agent-${agent.id}`} onClick={() => openEditDialog(agent)}>
                        <Edit size={14} />
                      </Button>
                      {agent.phone && (
                        <>
                          <Button size="sm" variant="outline" data-testid={`call-agent-${agent.id}`} title="Call" asChild>
                            <a href={`tel:${agent.phone}`}>
                              <Phone size={14} />
                            </a>
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`message-agent-${agent.id}`} title="Message" asChild>
                            <a href={`sms:${agent.phone}`}>
                              <MessageSquare size={14} />
                            </a>
                          </Button>
                        </>
                      )}
                      {agent.verificationStatus === 'PENDING' && (
                        <>
                          <Button size="sm" data-testid={`approve-agent-${agent.id}`} onClick={() => updateAgentStatus(agent.id, 'APPROVED')} className="bg-green-600 hover:bg-green-700 text-white">
                            <CheckCircle size={14} />
                          </Button>
                          <Button size="sm" variant="destructive" data-testid={`reject-agent-${agent.id}`} onClick={() => updateAgentStatus(agent.id, 'REJECTED')}>
                            <XCircle size={14} />
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        title="Delete rider"
                        data-testid={`delete-agent-${agent.id}`}
                        onClick={() => deleteAgent(agent)}
                        disabled={deletingAgentId === agent.id}
                        className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAgents.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No delivery agents match your filters</p>
          </div>
        )}
      </div>

      {/* AGENT-LIST PAGINATION */}
      {filteredAgents.length > PAGE_SIZE && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 px-1" data-testid="agents-pagination">
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold text-gray-900">
              {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredAgents.length)}
            </span> of <span className="font-semibold text-gray-900">{filteredAgents.length}</span>
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
            <span className="text-sm text-gray-700 px-2">Page <span className="font-semibold">{currentPage}</span> / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</Button>
          </div>
        </div>
      )}

      {/* DETAILS / ORDER HISTORY MODAL */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="agent-details-dialog">
          <DialogHeader>
            <DialogTitle>
              {detailsAgent?.name || 'Agent'}{' '}
              <span className="text-sm text-gray-500 ml-2">{detailsAgent?.phone}</span>
              {detailsAgent?.zone ? (
                <Badge className="ml-3 bg-blue-100 text-blue-800 border-0">{detailsAgent.zone.name}</Badge>
              ) : null}
            </DialogTitle>
          </DialogHeader>

          {/* AGENT MINI STATS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 mt-2">
            <div className="bg-orange-50 rounded-lg p-3" data-testid="agent-stat-today">
              <p className="text-xs text-gray-600 flex items-center gap-1"><ShoppingBag size={12} />Today</p>
              <p className="text-lg font-bold text-orange-700">₹{formatINR(detailsStats?.today?.revenue)}</p>
              <p className="text-xs text-gray-500">{detailsStats?.today?.count || 0} deliveries</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3" data-testid="agent-stat-yesterday">
              <p className="text-xs text-gray-600 flex items-center gap-1"><Calendar size={12} />Yesterday</p>
              <p className="text-lg font-bold text-blue-700">₹{formatINR(detailsStats?.yesterday?.revenue)}</p>
              <p className="text-xs text-gray-500">{detailsStats?.yesterday?.count || 0} deliveries</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3" data-testid="agent-stat-month">
              <p className="text-xs text-gray-600 flex items-center gap-1"><TrendingUp size={12} />This Month</p>
              <p className="text-lg font-bold text-emerald-700">₹{formatINR(detailsStats?.month?.revenue)}</p>
              <p className="text-xs text-gray-500">{detailsStats?.month?.count || 0} deliveries</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3" data-testid="agent-stat-all">
              <p className="text-xs text-gray-600 flex items-center gap-1"><IndianRupee size={12} />All Time</p>
              <p className="text-lg font-bold text-purple-700">₹{formatINR(detailsStats?.all?.revenue)}</p>
              <p className="text-xs text-gray-500">{detailsStats?.all?.count || 0} deliveries</p>
            </div>
          </div>

          {/* WALLET / EARNINGS SUMMARY */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            <div className="bg-sky-50 rounded-lg p-3 flex items-center gap-3">
              <WalletIcon className="text-sky-600" size={20} />
              <div>
                <p className="text-xs text-gray-600">Wallet</p>
                <p className="text-lg font-bold text-sky-700">₹{formatINR(detailsAgent?.wallet)}</p>
              </div>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3 flex items-center gap-3">
              <TrendingUp className="text-emerald-600" size={20} />
              <div>
                <p className="text-xs text-gray-600">Total Earnings</p>
                <p className="text-lg font-bold text-emerald-700">₹{formatINR(detailsAgent?.earningAmount)}</p>
              </div>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 flex items-center gap-3">
              <IndianRupee className="text-orange-600" size={20} />
              <div>
                <p className="text-xs text-gray-600">Collection Pending</p>
                <p className="text-lg font-bold text-orange-700">₹{formatINR(detailsAgent?.collectionAmount)}</p>
              </div>
            </div>
          </div>

          <Tabs value={detailsTab} onValueChange={setDetailsTab}>
            <div className="overflow-x-auto">
            <TabsList className="sm:grid sm:grid-cols-4 sm:w-full">
              <TabsTrigger value="orders" data-testid="agent-tab-orders">
                <ShoppingBag size={14} className="mr-2" />Order History ({detailsOrders.length})
              </TabsTrigger>
              <TabsTrigger value="wallet" data-testid="agent-tab-wallet">
                <WalletIcon size={14} className="mr-2" />Wallet Transactions ({detailsTxns.length})
              </TabsTrigger>
              <TabsTrigger value="withdrawals" data-testid="agent-tab-withdrawals">
                <IndianRupee size={14} className="mr-2" />Withdrawals ({detailsWithdrawals.length})
              </TabsTrigger>
              <TabsTrigger value="documents" data-testid="agent-tab-documents">
                <FileText size={14} className="mr-2" />Documents ({detailsDocuments.length})
              </TabsTrigger>
            </TabsList>
            </div>

            {/* ORDER HISTORY TAB */}
            <TabsContent value="orders" className="mt-4">
              {/* Filter bar inside modal */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search order #, customer, restaurant..."
                    className="pl-8 h-9"
                    value={oSearch}
                    onChange={(e) => setOSearch(e.target.value)}
                    data-testid="agent-orders-search"
                  />
                </div>
                <Select value={oRange} onValueChange={setORange}>
                  <SelectTrigger className="w-[140px] h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RANGES.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {oRange === 'custom' && (
                  <div className="flex items-center gap-1">
                    <Input type="date" className="h-9 w-[140px]" value={oCustomFrom} onChange={(e) => setOCustomFrom(e.target.value)} />
                    <span className="text-xs">to</span>
                    <Input type="date" className="h-9 w-[140px]" value={oCustomTo} onChange={(e) => setOCustomTo(e.target.value)} />
                  </div>
                )}
                <Select value={oStatus} onValueChange={setOStatus}>
                  <SelectTrigger className="w-[150px] h-9"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {Object.keys(ORDER_STATUS_COLORS).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={oRest} onValueChange={setORest}>
                  <SelectTrigger className="w-[170px] h-9"><SelectValue placeholder="Restaurant" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All restaurants</SelectItem>
                    {oRestaurantOptions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {(oSearch || oStatus !== 'all' || oRest !== 'all' || oRange !== 'all') && (
                  <Button variant="outline" size="sm" onClick={() => { setOSearch(''); setOStatus('all'); setORest('all'); setORange('all'); }}>
                    Clear
                  </Button>
                )}
                <div className="ml-auto text-xs text-gray-600">
                  <span className="font-semibold text-gray-900">{filteredAgentOrders.length}</span> · ₹{formatINR(filteredOrderRevenue)}
                </div>
              </div>

              {detailsLoading ? (
                <p className="text-center text-gray-500 py-6">Loading...</p>
              ) : filteredAgentOrders.length === 0 ? (
                <p className="text-center text-gray-500 py-6">No orders match your filters.</p>
              ) : (
                <>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm" data-testid="agent-orders-table">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Order #</th>
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-left">Customer</th>
                          <th className="px-3 py-2 text-left">Restaurant</th>
                          <th className="px-3 py-2 text-left">Total</th>
                          <th className="px-3 py-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {oPagedOrders.map((o) => (
                          <tr key={o._id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono text-xs">{o.orderNumber}</td>
                            <td className="px-3 py-2 text-xs">
                              {format(new Date(o.createdAt), 'dd MMM yyyy')}<br />
                              <span className="text-gray-500">{format(new Date(o.createdAt), 'hh:mm a')}</span>
                            </td>
                            <td className="px-3 py-2">
                              {o.user?.name || 'Guest'}
                              {o.user?.phone ? <p className="text-xs text-gray-500">{o.user.phone}</p> : null}
                            </td>
                            <td className="px-3 py-2">{o.resturant?.name || '-'}</td>
                            <td className="px-3 py-2 font-semibold">₹{formatINR(o.grandTotal)}</td>
                            <td className="px-3 py-2">
                              <Badge className={`${ORDER_STATUS_COLORS[o.status] || 'bg-gray-100 text-gray-700'} border-0`}>{o.status}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {filteredAgentOrders.length > PAGE_SIZE && (
                    <div className="flex items-center justify-between mt-3 text-sm">
                      <p className="text-gray-600">
                        Showing <span className="font-semibold text-gray-900">
                          {(oCurrentPage - 1) * PAGE_SIZE + 1}–{Math.min(oCurrentPage * PAGE_SIZE, filteredAgentOrders.length)}
                        </span> of {filteredAgentOrders.length}
                      </p>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" disabled={oCurrentPage <= 1} onClick={() => setOPage((p) => Math.max(1, p - 1))}>Previous</Button>
                        <span className="text-gray-700 px-2">Page <span className="font-semibold">{oCurrentPage}</span> / {oTotalPages}</span>
                        <Button variant="outline" size="sm" disabled={oCurrentPage >= oTotalPages} onClick={() => setOPage((p) => Math.min(oTotalPages, p + 1))}>Next</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            {/* WALLET TAB */}
            <TabsContent value="wallet" className="mt-4 space-y-4">
              {/* Collection summary + Max editor */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-lg border p-3 bg-orange-50">
                  <p className="text-xs text-gray-600 flex items-center gap-1">
                    <IndianRupee size={12} /> Collection Pending
                  </p>
                  <p className="text-xl font-bold text-orange-700">
                    ₹{formatINR(detailsAgent?.collectionAmount)}
                  </p>
                </div>

                <div className="rounded-lg border p-3 bg-amber-50" data-testid="agent-max-collection-card">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-gray-600">Max Collection Limit</p>
                    {!maxEditing && (
                      <button
                        type="button"
                        onClick={() => {
                          setMaxValue(
                            detailsAgent?.collectionAmountMax !== undefined &&
                            detailsAgent?.collectionAmountMax !== null
                              ? String(detailsAgent.collectionAmountMax)
                              : ''
                          );
                          setMaxEditing(true);
                        }}
                        data-testid="agent-edit-max-btn"
                        className="text-[11px] text-orange-700 hover:underline"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {!maxEditing ? (
                    <p className="text-xl font-bold text-amber-700">
                      ₹{formatINR(detailsAgent?.collectionAmountMax || 0)}
                    </p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={maxValue}
                        onChange={(e) => setMaxValue(e.target.value)}
                        className="h-8"
                        data-testid="agent-max-input"
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={saveMaxCollection}
                        disabled={maxSaving}
                        style={{ backgroundColor: '#C2410C' }}
                        className="text-white h-8"
                        data-testid="agent-max-save-btn"
                      >
                        {maxSaving ? '…' : 'Save'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setMaxEditing(false)}
                        className="h-8"
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  )}
                  {detailsAgent?.collectionAmountMax > 0 && (
                    <div className="mt-2 h-1.5 bg-amber-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          (detailsAgent.collectionAmount || 0) >= detailsAgent.collectionAmountMax
                            ? 'bg-red-500'
                            : 'bg-amber-600'
                        }`}
                        style={{
                          width: `${Math.min(
                            100,
                            ((detailsAgent.collectionAmount || 0) / detailsAgent.collectionAmountMax) * 100
                          )}%`
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="rounded-lg border p-3 bg-sky-50">
                  <p className="text-xs text-gray-600">Wallet Balance</p>
                  <p className="text-xl font-bold text-sky-700">
                    ₹{formatINR(detailsAgent?.wallet)}
                  </p>
                </div>
              </div>

              {/* Manual Transaction Form */}
              <form
                onSubmit={submitTransaction}
                className="border rounded-lg p-3 bg-gray-50 space-y-3"
                data-testid="agent-txn-form"
              >
                <p className="text-sm font-semibold text-gray-800">Record Transaction</p>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                  <div className="md:col-span-2">
                    <Label className="text-xs">Type</Label>
                    <Select
                      value={txnForm.type}
                      onValueChange={(v) =>
                        setTxnForm({
                          ...txnForm,
                          type: v,
                          paymentMethod:
                            v === 'COLLECTION_SETTLEMENT' ? 'CASH' : txnForm.paymentMethod
                        })
                      }
                    >
                      <SelectTrigger className="h-9" data-testid="agent-txn-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="COLLECTION_SETTLEMENT">Collection Settlement (-)</SelectItem>
                        <SelectItem value="COLLECTION_ADJUSTMENT">Collection Adjustment (+)</SelectItem>
                        <SelectItem value="WALLET_CREDIT">Wallet Credit (+)</SelectItem>
                        <SelectItem value="WALLET_DEBIT">Wallet Debit (-)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Amount (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={txnForm.amount}
                      onChange={(e) => setTxnForm({ ...txnForm, amount: e.target.value })}
                      className="h-9"
                      placeholder="0.00"
                      data-testid="agent-txn-amount"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Settlement date</Label>
                    <Input
                      type="date"
                      value={txnForm.settlementDate}
                      max={todayInputValue()}
                      onChange={(e) => setTxnForm({ ...txnForm, settlementDate: e.target.value })}
                      className="h-9"
                      data-testid="agent-txn-date"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Method</Label>
                    <Select
                      value={txnForm.paymentMethod}
                      onValueChange={(v) => setTxnForm({ ...txnForm, paymentMethod: v })}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CASH">Cash</SelectItem>
                        <SelectItem value="UPI">UPI</SelectItem>
                        <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
                        <SelectItem value="OTHER">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Note</Label>
                    <Input
                      value={txnForm.note}
                      onChange={(e) => setTxnForm({ ...txnForm, note: e.target.value })}
                      className="h-9"
                      placeholder="Optional"
                      data-testid="agent-txn-note"
                    />
                  </div>
                  <div className="md:col-span-2 flex items-end">
                    <Button
                      type="submit"
                      disabled={txnSaving}
                      style={{ backgroundColor: '#C2410C' }}
                      className="text-white w-full h-9"
                      data-testid="agent-txn-submit"
                    >
                      {txnSaving ? 'Saving…' : 'Record'}
                    </Button>
                  </div>
                </div>
                <p className="text-[11px] text-gray-500">
                  Settlements reduce <b>Collection Pending</b>. Wallet credits/debits adjust the agent wallet.
                </p>
              </form>

              {/* History */}
              {detailsLoading ? (
                <p className="text-center text-gray-500 py-6">Loading...</p>
              ) : detailsTxns.length === 0 ? (
                <p className="text-center text-gray-500 py-6">No transactions yet.</p>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm" data-testid="agent-wallet-table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Amount</th>
                        <th className="px-3 py-2 text-left">Method</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Order #</th>
                        <th className="px-3 py-2 text-left">Note</th>
                        <th className="px-3 py-2 text-left">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {detailsTxns.map((t) => (
                        <tr key={t.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <Badge className={`${TX_TYPE_COLORS[t.type] || 'bg-gray-100 text-gray-700'} border-0`}>{t.type || '-'}</Badge>
                          </td>
                          <td className="px-3 py-2 font-semibold">₹{formatINR(t.amount)}</td>
                          <td className="px-3 py-2">{t.paymentMethod || '-'}</td>
                          <td className="px-3 py-2">{t.status || '-'}</td>
                          <td className="px-3 py-2 font-mono text-xs">{t.orderNumber || '-'}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{t.note || '-'}</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap" title={t.createdAt ? `Recorded ${format(new Date(t.createdAt), 'dd MMM yyyy hh:mm a')}` : undefined}>
                            {t.settlementDate
                              ? format(new Date(t.settlementDate), 'dd MMM yyyy')
                              : (t.createdAt ? format(new Date(t.createdAt), 'dd MMM yyyy hh:mm a') : '-')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* WITHDRAWALS TAB */}
            <TabsContent value="withdrawals" className="mt-4 space-y-3">
              <p className="text-[11px] text-gray-500">
                Requested amounts are held from the agent's wallet immediately. Rejecting a
                request refunds the hold back to the wallet; marking it Paid logs a
                PARTNER_PAYOUT transaction.
              </p>

              {detailsLoading ? (
                <p className="text-center text-gray-500 py-6">Loading...</p>
              ) : detailsWithdrawals.length === 0 ? (
                <p className="text-center text-gray-500 py-6">No withdrawal requests yet.</p>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm" data-testid="agent-withdrawals-table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Amount</th>
                        <th className="px-3 py-2 text-left">Bank Details</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Remarks</th>
                        <th className="px-3 py-2 text-left">Requested</th>
                        <th className="px-3 py-2 text-left">Processed</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {detailsWithdrawals.map((w) => {
                        const isEditing = editingWithdrawalId === w.id;
                        const saving = withdrawSavingId === w.id;
                        return (
                          <tr key={w.id} className="hover:bg-gray-50 align-top">
                            <td className="px-3 py-2 font-semibold">₹{formatINR(w.amount)}</td>
                            <td className="px-3 py-2 text-xs text-gray-600">
                              {w.bankName || w.accountNo || w.ifscCode ? (
                                <>
                                  {w.bankName || '-'}<br />
                                  A/C: {w.accountNo || '-'}<br />
                                  IFSC: {w.ifscCode || '-'}
                                </>
                              ) : '-'}
                            </td>
                            <td className="px-3 py-2">
                              <Badge className={`${WITHDRAW_STATUS_COLORS[w.status] || 'bg-gray-100 text-gray-700'} border-0`}>
                                {w.status}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 min-w-[180px]">
                              {isEditing ? (
                                <div className="space-y-1">
                                  <Input
                                    className="h-8 text-xs"
                                    placeholder="Remarks"
                                    value={withdrawRemarksDraft}
                                    onChange={(e) => setWithdrawRemarksDraft(e.target.value)}
                                    data-testid={`withdraw-remarks-${w.id}`}
                                  />
                                  <Input
                                    className="h-8 text-xs"
                                    placeholder="Transaction ref (optional)"
                                    value={withdrawRefDraft}
                                    onChange={(e) => setWithdrawRefDraft(e.target.value)}
                                    data-testid={`withdraw-ref-${w.id}`}
                                  />
                                </div>
                              ) : (
                                <>
                                  <p className="text-xs text-gray-700">{w.remarks || '-'}</p>
                                  {w.transactionRef && (
                                    <p className="text-[11px] text-gray-400 font-mono">Ref: {w.transactionRef}</p>
                                  )}
                                </>
                              )}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {w.createdAt ? format(new Date(w.createdAt), 'dd MMM yyyy hh:mm a') : '-'}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {w.processedAt ? format(new Date(w.processedAt), 'dd MMM yyyy hh:mm a') : '-'}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex flex-col items-end gap-1">
                                {!isEditing && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => startEditingWithdrawal(w)}
                                    data-testid={`withdraw-edit-${w.id}`}
                                  >
                                    Edit remarks
                                  </Button>
                                )}
                                {isEditing && (
                                  <div className="flex flex-wrap justify-end gap-1">
                                    {w.status === 'PENDING' && (
                                      <>
                                        <Button
                                          size="sm"
                                          className="h-7 text-xs text-white"
                                          style={{ backgroundColor: '#16A34A' }}
                                          disabled={saving}
                                          onClick={() => updateWithdrawal(w, 'APPROVED')}
                                        >
                                          Approve
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="destructive"
                                          className="h-7 text-xs"
                                          disabled={saving}
                                          onClick={() => updateWithdrawal(w, 'REJECTED')}
                                        >
                                          Reject
                                        </Button>
                                      </>
                                    )}
                                    {w.status === 'APPROVED' && (
                                      <>
                                        <Button
                                          size="sm"
                                          className="h-7 text-xs text-white"
                                          style={{ backgroundColor: '#025043' }}
                                          disabled={saving}
                                          onClick={() => updateWithdrawal(w, 'PAID')}
                                        >
                                          Mark Paid
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="destructive"
                                          className="h-7 text-xs"
                                          disabled={saving}
                                          onClick={() => updateWithdrawal(w, 'REJECTED')}
                                        >
                                          Reject
                                        </Button>
                                      </>
                                    )}
                                    {w.status === 'REJECTED' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-xs"
                                        disabled={saving}
                                        onClick={() => updateWithdrawal(w, 'PENDING')}
                                      >
                                        Re-open
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 text-xs"
                                      disabled={saving}
                                      onClick={() => updateWithdrawal(w, w.status)}
                                    >
                                      {saving ? 'Saving…' : 'Save remarks'}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 text-xs"
                                      disabled={saving}
                                      onClick={() => setEditingWithdrawalId(null)}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* DOCUMENTS TAB */}
            <TabsContent value="documents" className="mt-4">
              {detailsLoading ? (
                <p className="text-center text-gray-500 py-6">Loading...</p>
              ) : detailsDocuments.length === 0 ? (
                <p className="text-center text-gray-500 py-6">No documents uploaded yet.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="agent-documents-grid">
                  {detailsDocuments.map((doc) => {
                    const isImage = /\.(jpe?g|png|gif|webp|heic)$/i.test(doc.url || '');
                    return (
                      <div
                        key={doc.field}
                        className="border rounded-lg overflow-hidden bg-gray-50"
                        data-testid={`agent-document-${doc.field}`}
                      >
                        <div className="h-32 bg-white flex items-center justify-center border-b">
                          {isImage ? (
                            <img
                              src={doc.url}
                              alt={doc.label}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <FileText size={36} className="text-gray-400" />
                          )}
                        </div>
                        <div className="p-2.5">
                          <p className="text-xs font-medium text-gray-800 mb-2 truncate" title={doc.label}>
                            {doc.label}
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs flex-1"
                              data-testid={`agent-document-view-${doc.field}`}
                              asChild
                            >
                              <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink size={12} className="mr-1" />View
                              </a>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs flex-1"
                              data-testid={`agent-document-download-${doc.field}`}
                              disabled={downloadingDoc === doc.field}
                              onClick={() => downloadDocument(doc)}
                            >
                              <Download size={12} className="mr-1" />
                              {downloadingDoc === doc.field ? '…' : 'Download'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* ---------------- DELETED PARTNERS ---------------- */}
      <Dialog open={showDeletedDialog} onOpenChange={setShowDeletedDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Deleted Partners</DialogTitle>
          </DialogHeader>

          <div className="relative mt-2 mb-4">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <Input
              type="text"
              placeholder="Search by name, phone or email..."
              value={deletedSearch}
              onChange={(e) => {
                setDeletedSearch(e.target.value);
                setDeletedPage(1);
              }}
              className="pl-9"
              data-testid="deleted-agents-search"
            />
          </div>

          {deletedLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : deletedAgents.length === 0 ? (
            <p className="text-center text-gray-500 py-12">No deleted partners found.</p>
          ) : (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[620px] text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      <th className="px-4 py-3">Partner</th>
                      <th className="px-4 py-3">Phone</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Vehicle</th>
                      <th className="px-4 py-3">Deleted On</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {deletedAgents.map((agent) => (
                      <tr key={agent.id} data-testid={`deleted-agent-row-${agent.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{agent.name || 'N/A'}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{agent.phone || '-'}</td>
                        <td className="px-4 py-3 text-gray-700">{agent.email || '-'}</td>
                        <td className="px-4 py-3 text-gray-700">
                          {agent.vehicleType || '-'}
                          {agent.vehicleNo ? <p className="text-xs text-gray-500">{agent.vehicleNo}</p> : null}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {agent.updatedAt
                            ? new Date(agent.updatedAt).toLocaleDateString()
                            : '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={restoringId === agent.id}
                            data-testid={`restore-agent-${agent.id}`}
                            onClick={() => handleRestore(agent)}
                          >
                            <RotateCcw size={14} className="mr-1" />
                            {restoringId === agent.id ? 'Restoring...' : 'Restore'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {deletedTotalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-gray-500">
                Page {deletedPage} of {deletedTotalPages} ({deletedTotal} total)
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={deletedPage <= 1}
                  onClick={() => setDeletedPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={deletedPage >= deletedTotalPages}
                  onClick={() => setDeletedPage((p) => Math.min(deletedTotalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryAgents;
